import _ from 'lodash';
import { makeObservable, override } from 'mobx';
import {
    AgentApiFindAllAgentsRequest,
    AgentDto,
    AgentSummaryDto,
    CreateAgentRequest,
    UpdateAgentRequest,
} from 'src/generated-api-client';
import { agentsApi, bpmsResourcesManagerApi } from 'src/services/apiServices';
import { ListFilter } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export type AgentsFilter = ListFilter<AgentApiFindAllAgentsRequest>;

export class AgentsStoreClass extends BasicStore<
    AgentSummaryDto,
    AgentsFilter,
    UpdateAgentRequest,
    CreateAgentRequest,
    AgentDto
> {
    constructor() {
        super();
        makeObservable(this);
    }

    itemDetailsFormKey = 'embedded:deployment:forms/bo/bo-26-agent.json';

    itemEditFormKey = 'embedded:deployment:forms/bo/bo-28-edit-agent.json';

    itemCreateFormKey = 'embedded:deployment:forms/bo/bo-27-create-agent.json';

    pager = new Pager();
    sorter = new Sorter();
    filterCriteria: FilterCriteria<AgentsFilter> = new FilterCriteria();

    api: BasicStoreApi<
        AgentSummaryDto,
        UpdateAgentRequest,
        CreateAgentRequest,
        AgentDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);
            const resp = await RequestHelper.unwrapFromAxiosPromise(
                agentsApi.findAllAgents(
                    {
                        ...this.pager,
                        ...this.sorter,
                        ...this.filterCriteria.filter,
                    },
                    options,
                ),
            );

            this.pager.setTotal(resp.totalElements || 0);

            return resp.content || [];
        },
        loadItemForm: (formKey: string, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                bpmsResourcesManagerApi.getForm({ formKey }, options),
            );
        },
        loadItem: (id: number, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                agentsApi.getAgent({ id }, options),
            );
        },
        updateItem: (id, data: UpdateAgentRequest, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                agentsApi.updateAgent(
                    { id: Number(id), updateAgentRequest: data },
                    options,
                ),
            );
        },
        createItem: (data: CreateAgentRequest, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                agentsApi.createAgent({ createAgentRequest: data }, options),
            );
        },
    };

    @override get hasFilter() {
        const { filterTaxNumber: taxId, filterSearch: search } =
            this.filterCriteria.filter;

        return !_.isNil(taxId) || !_.isNil(search);
    }
}

export const AgentsStore = new AgentsStoreClass();
